import React from 'react'
import { Box } from '@mui/material'
import { VisibilityOffRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

// The fixed drawing of the placeholder: soft shapes and grey lines, the same for every piece. Nothing here comes from the
// content it replaces (C15.6, RN127).
const BLOBS = [
  { left: '8%', top: '10%', width: '55%', height: '45%', background: '#5C7C8E' },
  { right: '4%', bottom: '8%', width: '50%', height: '50%', background: '#6B6B6B' },
  { left: '30%', bottom: 0, width: '40%', height: '30%', background: '#4D6A5E' }
]
const LINES = ['45%', '80%', '65%', '72%']

// Where the piece sits: a block (viewer, card, sheet, widget, marked screen), a list row, or a 110 px thumbnail (S243).
const MIN_HEIGHT = { block: 196, row: 0, small: 70 }

/**
 * Story 213, reopening 3 (PO 2026-10-23, K7, C15.6, design Sensible.dc.html) — what a protected piece shows while the
 * vigilance is covered in SENSITIVE_ONLY: a generic blurred drawing the size of the piece, with one small "Hidden" mark
 * and nothing else — no button, no tooltip, no reason (the camera icon of the top bar says it, S217).
 *
 * D4 (RN127): it takes NO prop of the content — only where it sits — and the content stays unmounted. The blur is applied
 * to its own fixed shapes, never to real content (a CSS blur over mounted content would still be readable with Inspect).
 */
const VisionBlurPlaceholder = ({ size = 'block' }) => {
  const { t_ } = useSmartTranslation()
  const small = size === 'small'

  return (
    <Box
      role='img'
      aria-label={t_('Hidden content')}
      data-testid='vision-blur-placeholder'
      data-size={size}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: size === 'block' ? 'auto' : '100%',
        minHeight: MIN_HEIGHT[size] || 0,
        borderRadius: small ? '8px' : '10px',
        bgcolor: '#353535',
        boxSizing: 'border-box'
      }}
    >
      <Box aria-hidden='true' sx={{ position: 'absolute', inset: '-20px', filter: 'blur(22px)', opacity: 0.9 }}>
        {BLOBS.map((blob, index) => (
          <Box key={index} component='span' sx={{ position: 'absolute', borderRadius: '999px', ...blob }} />
        ))}
      </Box>
      {!small && (
        <Box aria-hidden='true' sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '12px', p: '18px', filter: 'blur(5px)' }}>
          {LINES.map((width, index) => (
            <Box key={index} component='span' sx={{ height: '9px', width, borderRadius: '5px', bgcolor: '#6E6E6E' }} />
          ))}
        </Box>
      )}
      <Box
        component='span'
        data-testid='vision-blur-mark'
        sx={{
          position: 'absolute',
          ...(small ? { bottom: '6px', right: '6px', px: '6px', py: '2px', fontSize: 10 } : { top: '8px', right: '8px', px: '8px', py: '3px', fontSize: 11 }),
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          borderRadius: '999px',
          bgcolor: 'rgba(20, 20, 20, 0.55)',
          color: '#CFCFCF',
          fontWeight: 600,
          letterSpacing: '0.3px'
        }}
      >
        <VisibilityOffRounded data-testid='VisibilityOffIcon' sx={{ fontSize: small ? 11 : 12 }} />
        {t_('Hidden')}
      </Box>
    </Box>
  )
}

export default VisionBlurPlaceholder
